import { logOnce } from '../../log.utils';
import type { ColDef, ColGroupDef, ColumnState } from 'ag-grid-community';

export const AUTO_COLID = 'ag-Grid-AutoColumn';

export const isStringifiedNumber = (colId: string): boolean => /^\d+$/.test(colId);

export const isValidColId = (colId: string, gridColIds: string[]): boolean =>
  gridColIds.includes(colId) || colId === AUTO_COLID;

export type ColDefOrColGroupDef = (ColDef | ColGroupDef) & { colId?: string };

type ValidationResult = {
  issues: string[];
  validColIds: string[];
};

const validateColumns = (columns: (ColDefOrColGroupDef | ColumnState)[], gridColIds: string[]): ValidationResult => {
  const seen = new Set<string>();
  const issues = new Set<string>();
  const validColIds: string[] = [];

  for (const col of columns) {
    const colId = col.colId;
    if (!colId) continue;

    if (colId === AUTO_COLID) {
      validColIds.push(colId);
      continue;
    }

    if (isStringifiedNumber(colId)) {
      issues.add(colId);
    }

    if (!gridColIds.includes(colId)) {
      issues.add(colId);
    }

    if (seen.has(colId)) {
      issues.add(colId);
    } else {
      seen.add(colId);
      validColIds.push(colId);
    }
  }

  return { issues: Array.from(issues), validColIds };
};

export const validateColDefs = (cols: ColDefOrColGroupDef[], gridColIds: string[], gridId?: string): void => {
  const walk = (defs: ColDefOrColGroupDef[]): ColDefOrColGroupDef[] => {
    return defs.flatMap((col) => ('children' in col && col.children ? walk(col.children) : col));
  };

  const flatColumns = walk(cols);
  const { issues } = validateColumns(flatColumns, gridColIds);

  for (const issue of issues) {
    logOnce(`[${gridId}] ${issue}`);
  }
};

export const validatePresetColState = (presetCols: ColumnState[], gridColIds: string[]): string[] => {
  const { issues } = validateColumns(presetCols, gridColIds);
  return issues;
};
